import { FC, JSX } from 'react';
import { useParams } from 'react-router-dom';

import { BUTTON_TYPES, EvButton, EvMainTitle } from '@evinced-private/ui-common';

import { ErrorState } from 'src/components/common/ErrorState';
import { useProductsData } from 'src/hooks/useProductsData';
import { useReleaseNotesData } from 'src/hooks/useReleaseNotesData';
import { useReleaseNotesRouting } from 'src/hooks/useReleaseNotesRouting';

import { ContentArea } from './components/ContentArea';
import { ReleaseNotesNavigation } from './components/ReleaseNotesNavigation';
import { ReleaseNotesNavigationSkeleton } from './components/ReleaseNotesNavigationSkeleton';

import './ReleaseNotesPage.scss';

interface RouteParams {
	product?: string;
	subproduct?: string;
}

export const ReleaseNotesPage: FC = () => {
	const { product: productSlug, subproduct: subproductSlug } = useParams<RouteParams>();

	const {
		loading,
		error: productsError = true,
		topLevelProducts,
		retryProducts
	} = useProductsData();

	const currentProduct = useReleaseNotesRouting({
		topLevelProducts,
		productSlug,
		subProductSlug: subproductSlug
	});

	const {
		releaseNotes,
		releaseNotesLoading,
		releaseNotesError,
		isLoadingMore,
		hasMoreReleaseNotes,
		loadMoreReleaseNotes,
		retryReleaseNotes
	} = useReleaseNotesData({
		currentProduct
	});

	const renderPageHeader = (): JSX.Element => (
		<>
			<EvMainTitle className="title">What&apos;s new in Evinced</EvMainTitle>
			<p className="description">Learn about new Evinced features and enhancements.</p>
		</>
	);

	const renderContent = (): JSX.Element => {
		if (productsError) {
			return (
				<ErrorState
					message="We weren't able to load the &quot;What's New&quot; data. Please try again."
					onRetry={retryProducts}
				/>
			);
		}

		if (loading) {
			return (
				<div className="content-container">
					<ReleaseNotesNavigationSkeleton />
					<ContentArea title="Loading..." releaseNotes={[]} isLoading={true} />
				</div>
			);
		}

		return (
			<div className="content-container">
				<ReleaseNotesNavigation
					topLevelProducts={topLevelProducts}
					selectedProduct={currentProduct}
					subproductSlug={subproductSlug}
				/>

				{!currentProduct && (
					<div className="content-placeholder">
						<h2 className="content-title">Select a Product</h2>
						<p>Choose a product from the navigation to view its release notes.</p>
					</div>
				)}

				{currentProduct && releaseNotesError && (
					<ContentArea
						title={currentProduct.name}
						releaseNotes={[]}
						isLoading={false}
						isError={releaseNotesError}
					>
						<ErrorState
							message={`We weren't able to load the release notes for ${currentProduct.name}. Please try again.`}
							onRetry={retryReleaseNotes}
						/>
					</ContentArea>
				)}

				{currentProduct && !releaseNotesError && (
					<ContentArea
						title={currentProduct.name}
						releaseNotes={releaseNotes}
						isLoading={releaseNotesLoading}
					>
						{hasMoreReleaseNotes && !releaseNotesLoading && (
							<div className="load-more-container">
								<EvButton
									type={BUTTON_TYPES.PRIMARY}
									onClick={loadMoreReleaseNotes}
									loading={isLoadingMore}
									disabled={isLoadingMore}
								>
									Load More
								</EvButton>
							</div>
						)}
					</ContentArea>
				)}
			</div>
		);
	};

	return (
		<div className="release-notes-page">
			{renderPageHeader()}
			{renderContent()}
		</div>
	);
};
